import React, {Component} from 'react';
import {Alert, Button, Checkbox, Col, Icon, Modal, Pagination, Progress, Row, Spin, Table, Tooltip} from 'antd';
import autobind from 'autobind-decorator';
import {withTranslation, WithTranslation} from 'react-i18next'
import 'src/assets/custom.scss'
import {DataModel, Query} from '../../core/stores/dataStore';
import {clone} from '../../core/utils/object';
import {withSize} from 'react-sizeme';
import {CommandResult} from '../../core/stores/types';
import {isNumber} from 'util';

export interface CellData {
    key: string,
    yText: string,
    marked: boolean,
    isMarkAll: boolean,
    xKey: string,
    yKey: string,
    indexX: number,
    indexY: number,
}

export interface MarkAllCellData {
    value: boolean,
    yKey: string,
    indexY: number
}

export interface GridTableProps<T, U, Z> extends WithTranslation {
    xData: SpaceMap<T>;
    yData: SpaceMap<U>;
    cellData: SpaceMap<Z>; 
    xDataIndexZ: string; // dataIndex for Z key property ]
    yDataIndexZ: string; // dataIndex for Z key property ] this refers to a key property at Z object represented by cellData
    onRefresh: (xQuery: Query, yQuery: Query) => void;
    onSaveItem?: (xKey: string, yKey: string) => Promise<CommandResult<any>>;
    onDeleteItem?: (xKey: string, yKey: string, cellKey:string) => Promise<CommandResult<any>>;
    onDiscard?: (changes: Map<string, Map<string, boolean>>) => void;
    onSelectCell?: (cellData: CellData) => void;
    onSelectMarkAll?: (markAllCellData: MarkAllCellData) => void;
    rightToolbar?: React.ReactNode
    title?: string;
    isBusy?: boolean;
    hideRowSearchControl?: boolean;
    hideColumnSearchControl?: boolean;
    hideRowHeader?: boolean
    hideColumnHeader?: boolean;
    hideRowFilter?: boolean
    hideColumnFilter?: boolean;
    scroll?: {};
    initPagination?: boolean;
    hidePaginationX?: boolean;
    hidePaginationY?: boolean;
    pageSizeX?: number;
    pageSizeY?: number;
    readOnly?: boolean
}

export interface SpaceMap<T> {
    model: {
        query: Query,
        data: DataModel<T>;
        onQueryChanged?: (query: Query) => void;
    };
    alternativeDataSource?:T[],
    filter?: boolean,
    key: string;
    pageSize?: number;
    itemKey: string;
    title: React.ReactNode;
    render?: (record: T) => React.ReactNode;
    ellipsis?: boolean;
    itemText: string;
    fixed?: "left" | "center" | "right" | boolean,
    width?: number | string;
    height?: number;
    showSideways?: boolean;
    canSearch?: boolean;
    hit?: (rowItem: any, columnItem: any) => any;
    editor?: (rowItem: any, columnItem: any, columnKey: string, value: string) => React.ReactNode;
}

interface GridTableState {
    changes: Map<string, Map<string, boolean>>,
    dataSource?: any[],
    columns?:any[],
    isBusy?: boolean,
    showConfirmOnSaveModal?: boolean,
    showConfirmOnUndoModal?: boolean,

    //pagination
    pageSizeX: number;
    pageSizeY: number;
    activePageX: number;
    activePageY: number;
    totalPagesX: number;
    totalPagesY: number;
    totalItemsX: number;
    totalItemsY: number;

    //Saving changes 
    savingChanges: boolean;
    totalChangesElements: number;
    deleteElements: number;
    deleteElementsFail: number;
    deleteElementsSuccess: number;
    aditionElements: number;
    additionElementsFail: number;
    additionElementsSuccess: number;
    saveFinished: boolean;
}

//note: changes will save as first key the yKey, and then as second key the xKey. 
//the boolean value stored represents which operation it presents itself for.
//true: creation
//false: deletion
class GridTable<T, U, Z> extends Component<GridTableProps<T, U, Z>, GridTableState>{
    yKeyToIndex: Map<string, number>;
    xKeyToIndex: Map<string, number>;
    xyKeysToCellKey: Map<string, string>;
    originalZData: Map<string, Set<string>>;
    allColumnsCount: number;
    dataColumnsCount: number;

    constructor(props: GridTableProps<T, U, Z>) {
        super(props);
        this.state = {
            changes: new Map(),
            pageSizeX: props.xData.model && props.xData.model.query && props.xData.model.query.take ? props.xData.model.query.take : (props.pageSizeX ? props.pageSizeX : 50),
            pageSizeY: props.yData.model && props.yData.model.query && props.yData.model.query.take ? props.yData.model.query.take : (props.pageSizeY ? props.pageSizeY : 50),
            totalPagesX: props.xData.model && props.xData.model.data ? Math.ceil((props.xData.model.data == null ? 1 : props.xData.model.data.count) / 10) : 1,
            totalPagesY: props.yData.model && props.yData.model.data ? Math.ceil((props.yData.model.data == null ? 1 : props.yData.model.data.count) / 10) : 1,
            totalItemsX: props.xData.model && props.xData.model.data ? props.xData.model.data.count : 0,
            totalItemsY: props.yData.model && props.yData.model.data ? props.yData.model.data.count : 0,
            activePageX: 1,
            activePageY: 1,

            aditionElements: 0,
            additionElementsFail: 0,
            additionElementsSuccess: 0,
            deleteElements: 0,
            deleteElementsFail: 0,
            deleteElementsSuccess: 0,
            savingChanges: false,
            saveFinished: false,
            totalChangesElements: 0,
        }
        this.yKeyToIndex = new Map()
        this.xKeyToIndex = new Map()
        this.xyKeysToCellKey = new Map()
        this.originalZData = new Map()
        this.allColumnsCount = 0;
        this.dataColumnsCount = 0;
    }

    componentDidMount() {
        this.buildData(this.props.xData, this.props.yData, this.props.cellData)
    }
    
    componentWillReceiveProps(nextProps: GridTableProps<T, U, Z>) {
        this.yKeyToIndex = new Map()
        this.xKeyToIndex = new Map()
        this.originalZData = new Map()
        this.buildData(nextProps.xData, nextProps.yData, nextProps.cellData)
        this.setState({
            pageSizeX: nextProps.xData.model && nextProps.xData.model.query && nextProps.xData.model.query.take ? nextProps.xData.model.query.take : (nextProps.pageSizeX ? nextProps.pageSizeX : 50),
            pageSizeY: nextProps.yData.model && nextProps.yData.model.query && nextProps.yData.model.query.take ? nextProps.yData.model.query.take : (nextProps.pageSizeY ? nextProps.pageSizeY : 50),
            totalPagesX: nextProps.xData.model && nextProps.xData.model.data ? Math.ceil((nextProps.xData.model.data == null ? 1 : nextProps.xData.model.data.count) / 10) : 1,
            totalPagesY: nextProps.yData.model && nextProps.yData.model.data ? Math.ceil((nextProps.yData.model.data == null ? 1 : nextProps.yData.model.data.count) / 10) : 1,
            totalItemsX: nextProps.xData.model && nextProps.xData.model.data ? nextProps.xData.model.data.count : 0,
            totalItemsY: nextProps.yData.model && nextProps.yData.model.data ? nextProps.yData.model.data.count : 0,
        })
    }

    @autobind
    private buildData(xData: SpaceMap<T>, yData: SpaceMap<U>, cellData: SpaceMap<Z>) {
        this.setState({ isBusy: true })
        let columns = this.buildColumns(xData, yData)
        let zDataSet = this.buildZDataSet(cellData)
        let data = this.buildDataSource(xData, yData, zDataSet)
        this.originalZData = zDataSet
        this.setMarkAllValues(false, data, columns)
        this.setState({
            isBusy: false,
            columns: columns,
            dataSource: data as any,
        }) //Set mark all values after build
    }

    @autobind
    private buildZDataSet(cellData: SpaceMap<Z>) {
        //Build original data dictionary
        var zDataSet = new Map<string, Set<string>>()
        this.xyKeysToCellKey = new Map<string, string>()
        if (cellData.alternativeDataSource !== undefined) {
            cellData.alternativeDataSource.forEach(c => {
                var current = zDataSet.get((c as any)[this.props.yDataIndexZ])
                if (!current)
                    current = new Set<string>()
                current.add((c as any)[this.props.xDataIndexZ])
                zDataSet.set((c as any)[this.props.yDataIndexZ], current)
                this.xyKeysToCellKey.set((c as any)[this.props.xDataIndexZ] + '-' + (c as any)[this.props.yDataIndexZ], (c as any)[this.props.yData.itemKey])
            })
        } else {
            cellData.model.data.items.forEach(c => {
                var current = zDataSet.get((c.item as any)[this.props.yDataIndexZ])
                if (!current)
                    current = new Set<string>()
                current.add((c.item as any)[this.props.xDataIndexZ])
                zDataSet.set((c.item as any)[this.props.yDataIndexZ], current)
                this.xyKeysToCellKey.set((c as any)[this.props.xDataIndexZ] + '-' + (c as any)[this.props.yDataIndexZ], (c as any)[this.props.yData.itemKey])
            })
        }
        return zDataSet
    }

    @autobind
    private buildColumns(xData: SpaceMap<T>, yData: SpaceMap<U>) {
        const { t } = this.props

        var columns: any[] = [
            {
                title: (
                    <div style={{ width: yData.width }}>
                        { xData.showSideways &&
                            <div style={{ marginTop: (xData.height && isNumber(xData.height)) ? xData.height - 40 : 80 }}>
                                {yData.title}
                            </div>
                        }
                        {!xData.showSideways &&
                            <div>
                                {yData.title}
                            </div>
                        }
                    </div>
                ),
                dataIndex: 'yText',
                isDataColumn: false,
                width: yData.width,
                fixed: yData.fixed,
                ellipsis: yData.ellipsis,
                render: (title: string) => (
                    <div style={{ width: yData.width, wordWrap: 'break-word', wordBreak: 'break-word', msTextOverflow: "ellipsis", OTextOverflow: "ellipsis" }}>
                        {title}
                    </div>)
                
            },
            {
                title: (
                    <>
                        { xData.showSideways &&
                            <div className="rotate-180" style={{ height: xData.height, OTextOverflow: "ellipsis", writingMode: "vertical-rl", textOrientation: "sideways" }}>
                                {t('Mark All')}
                            </div>
                        }
                        {!xData.showSideways &&
                            <div style={{ msTextOverflow: "ellipsis", OTextOverflow: "ellipsis" }}>
                                {t('Mark All')}
                            </div>
                        }
                    </>
                ),
                dataIndex: 'markedAll',
                isDataColumn: false,
                fixed: yData.fixed,
                align: "left",
                width: 50,
                render: (data: any) => {
                    if (!(data.indexX == 0 && data.indexY == 0))
                        return <Checkbox indeterminate={data.value} checked={data.value} onChange={() => this.selectMarkAllY(data)} />
                }
            }
        ]
        columns.push(...xData.model.data.items.map(x => {
            return {
                title: (
                    <>
                        {xData.showSideways &&
                            <div className="rotate-180" style={{ height: xData.height, OTextOverflow: "ellipsis", writingMode: "vertical-rl", textOrientation: "sideways" }}>
                            <Tooltip title={(x.item as any)[xData.itemText]}><span>{(x.item as any)[xData.itemText].replace(/\s/g, '').split(/[()]/g)[0].length > 0 ? (x.item as any)[xData.itemText].split(/[()]/g)[0] : (x.item as any)[xData.itemText] }</span></Tooltip>
                            </div>
                        }
                        {!xData.showSideways &&
                            <div title={(x.item as any)[xData.itemText]} style={{ height: xData.height, OTextOverflow: "ellipsis"}}>
                                {(x.item as any)[xData.itemText]}
                            </div>
                        }
                    </>),
                dataIndex: (x.item as any)[xData.itemKey],
                key: (x.item as any)[xData.itemKey],
                isDataColumn: true,
                align: "left",
                width: xData.width,
                ellipsis: false,
                render: (data: any) => {
                    if (data.isMarkAll)
                        return <Checkbox checked={data.marked} indeterminate={data.marked} onChange={() => this.selectMarkAllX(data)} />
                    return <Checkbox checked={data.marked} onChange={() => this.selectCell(data)} />
                }
            }
        }))
        this.dataColumnsCount = columns.length - 2

        if (!yData.fixed) { //align all to left
            columns.push({
                title: "",
                isDataColumn: false,
                align: "left",
            })
        }

        this.allColumnsCount = columns.length
        return columns
    }

    @autobind
    private buildDataSource(xData: SpaceMap<T>, yData: SpaceMap<U>,zDataSet: Map<string, Set<string>>) {
        let changes = this.state.changes
        const { t } = this.props
        const data: any[] = [];

        //create and fill first data row (Mark All)
        var dataMarkAllRow:any = {
            key: "markAllRow",
            yText: t("Mark All"),
            markedAll: {
                value: false,
                yKey: "markAllRow",
                indexY: 0,
                indexX: 0
            }
        }
        xData.model.data.items.forEach((x, j) => {
            dataMarkAllRow[(x.item as any)[xData.itemKey]] = {
                marked: false,
                isMarkAll: true,
                xKey: (x.item as any)[xData.itemKey],
                yKey: "markAllRow",
                indexX: j + 1,
                indexY: 0
            }
        })
        data.push(dataMarkAllRow)

        //create and fill data rows
        yData.model.data.items.forEach((y, i) => {
            var dataItem: any = {
                key: (y.item as any)[yData.itemKey],
                yText: (y.item as any)[yData.itemText],
                markedAll: false
            }

            xData.model.data.items.forEach((x, j) => {
                //set original value as default
                let markedSet = zDataSet.get((y.item as any)[yData.itemKey])
                var marked = markedSet !== undefined && markedSet.firstOrDefault(c => c == (x.item as any)[xData.itemKey]) !== null

                let changesMarkedSet = changes.get((y.item as any)[yData.itemKey])
                if (changesMarkedSet !== undefined && changesMarkedSet.has((x.item as any)[xData.itemKey])) {
                    //update value if changed
                    marked = changesMarkedSet.get((x.item as any)[xData.itemKey]) === true
                }

                dataItem[(x.item as any)[xData.itemKey]] = {
                    marked: marked,
                    isMarkAll: false,
                    xKey: (x.item as any)[xData.itemKey],
                    yKey: (y.item as any)[yData.itemKey],
                    indexX: j+1,
                    indexY: i+1
                }

                //set index for x item key
                this.xKeyToIndex.set((x.item as any)[xData.itemKey], j + 1)
            })

            dataItem.markedAll = {
                value: false, //default value for mark all checkboxes
                yKey: (y.item as any)[yData.itemKey],
                indexY: i + 1,
                indexX: 0
            }
            data.push(dataItem)

            //set index for y item key
            this.yKeyToIndex.set((y.item as any)[yData.itemKey], i + 1)
        })
        return data
    }

    @autobind
    private selectMarkAllY(data: any) {
        if (this.props.readOnly)
            return;

        if (this.state.dataSource && this.state.columns) {
            var changes = this.state.changes
            var originals = this.originalZData
            var changesMarkedSet = changes.get(data["yKey"])
            let originalValueSet = originals.get(data["yKey"])
            var dataSource = [...this.state.dataSource]
            var row = dataSource[data.indexY]

            //set new value for mark all cell
            let newVal = !data.value
            row["markedAll"].value = newVal

            //set new value for all row
            this.state.columns.forEach(column => {
                if (column.isDataColumn) {
                    let isOriginalMarked = originalValueSet && originalValueSet.has(column.key)
                    if (newVal) { //if checkbox is now marked
                        if (!isOriginalMarked) {
                            //add change if original was not marked
                            if (changesMarkedSet) {
                                changesMarkedSet.set(column.key, true)
                            } else {
                                changesMarkedSet = new Map<string, boolean>()
                                changesMarkedSet.set(column.key, true)
                            }
                        } else {
                            if (changesMarkedSet) {
                                changesMarkedSet.delete(column.key)
                                if (changesMarkedSet.size == 0) {
                                    changes.delete(data["yKey"])
                                }
                            }
                        }
                    }
                    else {
                        if (isOriginalMarked) {
                            //add change if original was marked
                            if (changesMarkedSet) {
                                changesMarkedSet.set(column.key, false)
                            } else {
                                changesMarkedSet = new Map<string, boolean>()
                                changesMarkedSet.set(column.key, false)
                            }
                        } else {
                            if (changesMarkedSet) {
                                changesMarkedSet.delete(column.key)
                                if (changesMarkedSet.size == 0) {
                                    changes.delete(data["yKey"])
                                }
                            }
                        }
                    }
                    row[column.key].marked = newVal
                }
            })
            if (changesMarkedSet && changesMarkedSet.size > 0) {
                changes.set(data["yKey"], changesMarkedSet)
            } else {
                changes.delete(data["yKey"])
            }
            this.setMarkAllValues(false)
            this.setState({ changes: changes })
        }
    }

    @autobind
    private selectMarkAllX(data: any) {
        if (this.props.readOnly)
            return;

        if (this.state.dataSource && this.state.columns) {
            var changes = this.state.changes
            var dataSource = [...this.state.dataSource]

            //set new value for mark all cell
            let newVal = !data.marked
            dataSource[data.indexY][data.xKey].marked = newVal

            dataSource.forEach((row, i) => {
                //only apply if row represents data (skip first row)
                if (i > 0) {
                    var changesMarkedSet = changes.get(row.key)
                    let isOriginalMarked = this.getOriginalCellValue(data.xKey, row.key)
                    if (newVal) {
                        if (!isOriginalMarked) {
                            //add change if original was not marked
                            if (changesMarkedSet) {
                                changesMarkedSet.set(data.xKey, true)
                            } else {
                                changesMarkedSet = new Map<string, boolean>()
                                changesMarkedSet.set(data.xKey, true)
                            }
                        } else {
                            if (changesMarkedSet) {
                                changesMarkedSet.delete(data.xKey)
                            }
                        }
                    } else {
                        if (isOriginalMarked) {
                            //add change if original was marked
                            if (changesMarkedSet) {
                                changesMarkedSet.set(data.xKey, false)
                            } else {
                                changesMarkedSet = new Map<string, boolean>()
                                changesMarkedSet.set(data.xKey, false)
                            }
                        } else {
                            if (changesMarkedSet) {
                                changesMarkedSet.delete(data.xKey)
                            }
                        }
                    }
                    row[data.xKey].marked = newVal

                    if (changesMarkedSet && changesMarkedSet.size > 0) {
                        changes.set(row.key, changesMarkedSet)
                    } else {
                        changes.delete(row.key)
                    }
                }
            })
            this.setMarkAllValues(false)
            this.setState({ changes: changes })
        }
    }

    @autobind
    private selectCell(data: any) {
        if (this.props.readOnly)
            return;

        if (this.state.dataSource) {
            var dataSource = [...this.state.dataSource]
            var row = dataSource[data.indexY]
            var changes = this.state.changes
            var originals = this.originalZData
            let changesMarkedSet = changes.get(data["yKey"])
            let originalValueSet = originals.get(data["yKey"])
            let isOriginalMarked = originalValueSet && originalValueSet.has(data["xKey"])
            
            if (!data.marked) { //if checkbox is now marked
                if (!isOriginalMarked) {
                    //add change if original was not marked
                    if (changesMarkedSet) {
                        changesMarkedSet.set(data["xKey"], true)
                    } else {
                        let val = new Map<string, boolean>()
                        val.set(data["xKey"], true)
                        changes.set(data["yKey"], val)
                    }
                }
                else {
                    if (changesMarkedSet) {//delete change if original was marked
                        changesMarkedSet.delete(data["xKey"])
                        if (changesMarkedSet.size == 0) {
                            changes.delete(data["yKey"])
                        }
                    }
                }
            }
            else {
                if (isOriginalMarked) {
                    //add change if original was marked
                    if (changesMarkedSet) {
                        changesMarkedSet.set(data["xKey"], false)
                    } else {
                        let val = new Map<string, boolean>()
                        val.set(data["xKey"], false)
                        changes.set(data["yKey"], val)
                    }
                    
                } else {
                    if (changesMarkedSet) {//delete change if original was not marked
                        changesMarkedSet.delete(data["xKey"])
                        if (changesMarkedSet.size == 0) {
                            changes.delete(data["yKey"])
                        }
                    }
                }
            }

            dataSource[data["indexY"]][data["xKey"]].marked = data.marked ? false : true
            this.setMarkAllValues(false)
            this.setState({ changes: changes })
        }
    }

    @autobind
    private isSelectedAllColumn(xKey: string, originalValue?: boolean, data?: any[]) {
        if (data === undefined) {
            var data = this.state.dataSource
        }
        var result = false
        if (data) {
            var selectedCount = 1
            let getValueFunction = (originalValue) ? this.getOriginalCellValue : this.getCurrentCellValue;
            data.forEach(row => {
                if (getValueFunction(xKey, row.key))
                    selectedCount++
            })
            result = selectedCount == data.length
        }
        return result
    }

    @autobind
    private isSelectedAllRow(yKey: string, originalValue?: boolean, data?: any[], columns?: any[]) {
        if (data === undefined) {
            var data = this.state.dataSource
        }
        if (columns === undefined) {
            var columns = this.state.columns
        }
        var result = false
        if (data && columns) {
            var selectedCount = this.allColumnsCount - this.dataColumnsCount //there are 2 non data columns
            let getValueFunction = (originalValue) ? this.getOriginalCellValue : this.getCurrentCellValue;
            columns.forEach(column => {
                if (getValueFunction(column.key, yKey))
                    selectedCount++
            })
            result = selectedCount == columns.length
        }
        return result
    }

    @autobind
    private getCurrentCellValue(xKey: string, yKey: string) {
        let changesValueSet = this.state.changes.get(yKey)
        if (changesValueSet !== undefined && changesValueSet.has(xKey)) {
            return changesValueSet.get(xKey) === true
        }
        return this.getOriginalCellValue(xKey, yKey)
    }

    @autobind
    private getOriginalCellValue(xKey:string, yKey:string) {
        let originalValueSet = this.originalZData.get(yKey)
        return originalValueSet !== undefined && originalValueSet.has(xKey)
    }

    @autobind
    private setMarkAllValues(originalValues?: boolean, data?: any[], columns?: any[]) {
        if (data === undefined) {
            data = this.state.dataSource
        }

        //Reset mark all checkboxes
        var xKeys = Array.from(this.xKeyToIndex.keys())
        var yKeys = Array.from(this.yKeyToIndex.keys())

        xKeys.forEach(key => {
            if (data) {
                data[0][key].marked = this.isSelectedAllColumn(key, originalValues, data)
            }
        })
        yKeys.forEach(key => {
            let index = this.yKeyToIndex.get(key)
            if (data && index) {
                data[index]["markedAll"].value = this.isSelectedAllRow(key, originalValues, data, columns)
            }
        })
    }

    @autobind
    private undoChanges() {
        let changes = this.state.changes
        let data = this.state.dataSource
        if (changes) {
            changes.forEach((value, yKey) => {
                if (yKey != "markAllRow") {
                    value.forEach((marked, xKey) => {
                        var index = this.yKeyToIndex.get(yKey);
                        if (index && data) {
                            let originalValue = this.getOriginalCellValue(xKey, yKey)
                            data[index][xKey].marked = originalValue

                            if (!originalValue) {
                                data[index].markedAll.value = false
                            }
                        }
                    })
                }
            })
        }
        this.setMarkAllValues(true)
        this.setState({ changes: new Map() })
    }

    @autobind
    private onChangePageSizeX(pageSize: number) {
        if (pageSize !== this.state.pageSizeX) {
            this.setState({
                pageSizeX: pageSize,
                activePageX: 1
            });

            var query = clone<Query>(this.props.xData.model.query);

            query.skip = 0;
            query.take = pageSize;

            this.props.xData.model.query = query;

            this.setState({ changes: new Map() })
            if (this.props.xData.model.onQueryChanged)
                this.props.xData.model.onQueryChanged(query);
        }
    }

    @autobind
    private onChangePageSizeY(pageSize: number) {
        if (pageSize !== this.state.pageSizeY) {
            this.setState({
                pageSizeY: pageSize,
                activePageY: 1
            });

            var query = clone<Query>(this.props.yData.model.query);

            query.skip = 0;
            query.take = pageSize;

            this.props.yData.model.query = query;

            this.setState({ changes: new Map() })
            if (this.props.yData.model.onQueryChanged)
                this.props.yData.model.onQueryChanged(query);
        }
    }

    @autobind
    private onChangePageX(page: number) {
        this.undoChanges()
        if (page !== this.state.activePageX) {
            this.setState({
                activePageX: page
            });

            var query = clone<Query>(this.props.xData.model.query);

            query.skip = (page - 1) * this.state.pageSizeX;
            query.take = this.state.pageSizeX;

            this.props.xData.model.query = query;

            this.setState({ changes: new Map() })
            if (this.props.xData.model.onQueryChanged)
                this.props.xData.model.onQueryChanged(query);
        }
    }

    @autobind
    private onChangePageY(page: number) {
        this.undoChanges()
        if (page !== this.state.activePageY) {
            this.setState({
                activePageY: page
            });

            var query = clone<Query>(this.props.yData.model.query);

            query.skip = (page - 1) * this.state.pageSizeY;
            query.take = this.state.pageSizeY;

            this.props.yData.model.query = query;

            this.setState({ changes: new Map() })
            if (this.props.yData.model.onQueryChanged)
                this.props.yData.model.onQueryChanged(query);
        }
    }

    @autobind
    private getChangesCount() { //returns [positive, negative, total]
        let changes = this.state.changes

        var positiveCount = 0
        var negativeCount = 0
        var totalCount = 0

        changes.forEach((xChanges, yKey) => {
            xChanges.forEach((val, xKey) => {
                if (val)
                    positiveCount++
                else
                    negativeCount++
                totalCount++
            })
        })
        return [positiveCount, negativeCount, totalCount]
    }

    @autobind
    private async onSaveChanges() {
        let changes = this.state.changes
        let changesCount = this.getChangesCount()

        var addOk = 0
        var addFail = 0
        var delOk = 0
        var delFail = 0

        this.setState({
            isBusy: true,
            showConfirmOnSaveModal: false,
            totalChangesElements: changesCount[2],
            additionElementsSuccess: 0,
            additionElementsFail: 0,
            deleteElementsSuccess: 0,
            deleteElementsFail: 0,
            savingChanges: true,
            saveFinished: false
        })

        if (changesCount[2]) {
            let yKeyArray = Array.from(changes.keys())
            for (var i = 0; i < yKeyArray.length; i++) {

                let yKey = yKeyArray[i]
                let xChanges = changes.get(yKey)
                if (xChanges) {
                    let xKeyArray = Array.from(xChanges.keys())
                    for (var j = 0; j < xKeyArray.length; j++) {

                        let xKey = xKeyArray[j]
                        let val = xChanges.get(xKey)
                        if (val) { //change represents addition
                            if (this.props.onSaveItem) {
                                var result = await this.props.onSaveItem(xKey, yKey).catch(reason => {
                                    addFail++
                                })
                                if (result && result.isSuccess) {
                                    addOk++
                                }
                            } else {
                                addFail++
                            }
                        } else { //change represents deletion
                            if (this.props.onDeleteItem) {
                                var cellKey = this.xyKeysToCellKey.get(xKey + '-' + yKey)
                                if (cellKey) {
                                    var result = await this.props.onDeleteItem(xKey, yKey, cellKey).catch(reason => {
                                        delFail++
                                    })
                                    if (result && result.isSuccess) {
                                        delOk++
                                    }
                                } else {
                                    delFail++
                                }
                            } else {
                                delFail++
                            }
                        }
                        this.setState({
                            additionElementsSuccess: addOk,
                            additionElementsFail: addFail,
                            deleteElementsSuccess: delOk,
                            deleteElementsFail: delFail,
                        })
                    }
                }
            }
        }

        this.onRefresh()
        this.setState({
            isBusy: false,
            saveFinished: true
        })

    }

    @autobind
    private async onRefresh() { 
        this.setState({ changes: new Map() })
        this.props.onRefresh(this.props.xData.model.query, this.props.yData.model.query)
    }

    render() {
        const { t } = this.props

        var width = (this.props as any).size.width;
        var height = (this.props as any).size.height;
        var isMobile = width < 480;
        var isTablet = width >= 480 && width < 1024;

        let paginationX = {
            size: (isMobile || isTablet) ? 'small' : undefined,
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            onShowSizeChange: (_: number, pageSize: number) => {
                this.onChangePageSizeX(pageSize);
            },
            onChange: (page: number) => {
                this.onChangePageX(page);
            },
            pageSize: this.state.pageSizeX,
            total: this.state.totalItemsX,
            showTotal: (total: number, range: any) => isMobile ? '' : `${range[0]} ${t('to')} ${range[1]} ${t('of')} ${total}`,
            current: this.state.activePageX
        };
        let paginationY = {
            size: (isMobile || isTablet) ? 'small' : undefined,
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            onShowSizeChange: (_: number, pageSize: number) => {
                this.onChangePageSizeY(pageSize);
            },
            onChange: (page: number) => {
                this.onChangePageY(page);
            },
            pageSize: this.state.pageSizeY,
            total: this.state.totalItemsY,
            showTotal: (total: number, range: any) => isMobile ? '' : `${range[0]} ${t('to')} ${range[1]} ${t('of')} ${total}`,
            current: this.state.activePageY
        };

        let showPaginationX = this.props.xData.model.data.items.length < paginationX.total
        let showPaginationY = this.props.yData.model.data.items.length < paginationY.total

        let columnsWidthSum = this.state.columns?.sum(x => x.isDataColumn ? x.width : 0)
        var scrollX = false;
        if (columnsWidthSum && columnsWidthSum > width - 300) {
            scrollX = true;
        }

        let rowHeightSum = this.state.dataSource ? this.state.dataSource.length * 54 : 0
        var scrollY = false;
        if (rowHeightSum > 500) {
            scrollY = true
        }

        let changesCount = this.getChangesCount()
        return (
            <div>
                <Modal
                    visible={this.state.showConfirmOnSaveModal}
                    closable={false}
                    onOk={this.onSaveChanges}
                    onCancel={() => this.setState({ showConfirmOnSaveModal: false })}
                    okText={t("Ok")}
                    cancelText={t("Cancel")}
                    title={t('Confirmation required')}>
                    <div>
                        <p>
                            {t("The following changes will be applied")}
                        </p>
                        <ul>
                            <li>
                                {changesCount[0].toString() + " " + t('Additions')}
                            </li>

                            <li>
                                {changesCount[1].toString() + " " + t('Deletions')}
                            </li>
                        </ul>
                    </div>
                </Modal>

                <Modal
                    visible={this.state.savingChanges}
                    closable={false}
                    onOk={() => this.setState({ savingChanges: false })}
                    onCancel={() => this.setState({ savingChanges: false })}
                    okText={t("Ok")}
                    cancelButtonProps={{ hidden: true }}
                    okButtonProps={{ hidden: !this.state.saveFinished && (this.state.deleteElementsFail == 0 && this.state.additionElementsFail == 0) }}
                    title={t('Save changes')}>
                    <Spin spinning={!this.state.saveFinished} tip={t("Saving changes...")}>
                        <div>
                            <div style={{ textAlign: "center" }}>
                                <Progress type="circle" percent={this.state.totalChangesElements == 0 ? 0 : Math.round((this.state.deleteElementsSuccess + this.state.deleteElementsFail + this.state.additionElementsSuccess + this.state.additionElementsFail) * 100 / this.state.totalChangesElements)} />
                            </div>
                            <>
                                <Alert message={<p>{t("Added elements")} : {this.state.additionElementsSuccess}</p>} banner type="success" showIcon />
                                {this.state.additionElementsFail > 0 && <Alert message={<p>{t("Failed to add")} : {this.state.additionElementsFail}</p>} banner type="error" showIcon />}

                                <Alert message={<p>{t("Deleted elements")} : {this.state.deleteElementsSuccess}</p>} banner type="info" showIcon />
                                {this.state.deleteElementsFail > 0 && <Alert message={<p>{t("Failed to delete")} : {this.state.deleteElementsFail}</p>} banner type="error" showIcon />}
                            </>
                        </div>
                    </Spin>

                </Modal>

                <Row gutter={10} style={{ marginBottom: 10 }} type="flex" justify="end">
                    <Col>
                        <Button onClick={this.onRefresh} style={{ paddingLeft: 7, paddingRight: 7, width: 32, height: 32 }}>
                            <Icon type='reload' />
                        </Button>
                    </Col>
                    {!this.props.hidePaginationX && showPaginationX &&
                        <Col>
                            <Pagination {...paginationX} />
                        </Col>
                    }
                </Row>

                <div>
                    <Table
                        dataSource={this.state.dataSource}
                        columns={this.state.columns}
                        pagination={false}
                        loading={this.props.xData.model.data.isBusy || this.props.yData.model.data.isBusy || this.props.cellData.model.data.isBusy || this.state.isBusy}
                        locale={{
                            emptyText: t('No data'),
                            filterReset: t('Reset filter'),
                            selectAll: t('Select all'),
                            filterConfirm: t('Filter confirm'),
                            filterTitle: t('Filter'),
                            selectInvert: t("Invert selection"),
                            sortTitle: t('Sort')
                        }}
                        useFixedHeader
                        scroll={{ x: (scrollX) ? 1500 : undefined, y: (scrollY) ? 500 : undefined }}
                    />
                </div>
                {!this.props.hidePaginationY && showPaginationY &&
                    <div style={{ float: "left", marginTop: 10 }}>
                        <Pagination {...paginationY} />
                    </div>
                }

                {this.state.changes && this.state.changes.size > 0 && !(this.props.readOnly) &&
                    <div style={{ float: "right", marginTop: 10 }}>
                        <div style={{ marginLeft: 10, float: "right" }}>
                            <Button type="primary" onClick={() => { this.setState({ showConfirmOnSaveModal: true }) }}>
                                {t("Save")}
                            </Button>
                        </div>
                        <div style={{ float: "right" }}>
                            <Button type="danger" onClick={this.undoChanges}>
                                {t("Undo")}
                            </Button>
                        </div>
                    </div>
                }
            </div>)
    }
}

export const GridTableView = withTranslation()(withSize({ monitorHeight: true })(GridTable)) as any;
